import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthResponse, Role, UserProfile } from '@motoschool/shared';
import { api, tokenStorage } from '../lib/api';

interface AuthContextValue {
  user: UserProfile | null;
  isLoading: boolean;
  login: (login: string, password: string) => Promise<void>;
  /** Вход по ссылке из кабинета: пароль не нужен, секрет — сам токен. */
  loginByLink: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Часовой пояс для отображения времени: берём из профиля. */
  timeZone: string;
  hasRole: (...roles: Role[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const loadUser = useCallback(async () => {
    if (!tokenStorage.access) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const data = await api.get<{ user: UserProfile }>('/auth/me');
      setUser(data.user);
    } catch {
      // Токен протух и обновить его не вышло — считаем сессию закрытой.
      tokenStorage.clear();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  const login = useCallback(
    async (loginValue: string, password: string) => {
      const data = await api.post<AuthResponse>(
        '/auth/login',
        { login: loginValue, password },
        { anonymous: true },
      );
      tokenStorage.save(data.tokens);
      setUser(data.user);
      await queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const loginByLink = useCallback(
    async (token: string) => {
      const data = await api.post<AuthResponse>('/auth/link', { token }, { anonymous: true });
      tokenStorage.save(data.tokens);
      setUser(data.user);
      await queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    const refresh = tokenStorage.refresh;
    tokenStorage.clear();
    setUser(null);
    queryClient.clear();

    // Отзываем токен на сервере, но выход не должен зависеть от сети.
    if (refresh) {
      await api.post('/auth/logout', { refreshToken: refresh }, { anonymous: true }).catch(() => {
        /* сессия всё равно закрыта локально */
      });
    }
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      login,
      loginByLink,
      logout,
      refreshUser: loadUser,
      timeZone: user?.timeZone ?? 'Asia/Almaty',
      hasRole: (...roles: Role[]) => (user ? roles.includes(user.role) : false),
    }),
    [user, isLoading, login, loginByLink, logout, loadUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth должен вызываться внутри AuthProvider');
  return context;
}
